import { useEffect, useState } from 'react';
import { Box, Button, Snackbar, Tab, Tabs, TextField, Typography, useTheme } from '@mui/material';
import { tokens } from '../../theme';
import Header from '../../components/Header';
import { useParams, Link } from 'react-router-dom';

/**
 * Dark theme uses primary.main ≈ page background; MUI focuses InputLabels with primary, so labels disappear.
 * Force readable label + border colors for outlined fields on this screen.
 */
function tripReportOutlinedTextFieldProps(colors, theme, extraSx = {}) {
  const isDark = theme.palette.mode === 'dark';
  const notchBg = isDark ? colors.primary[500] : theme.palette.background.default;
  const labelColor = isDark ? colors.grey[200] : colors.grey[800];
  const inputColor = isDark ? colors.grey[100] : colors.grey[900];
  const borderMuted = isDark ? colors.grey[600] : colors.grey[400];
  const borderHover = isDark ? colors.grey[500] : colors.grey[600];
  const labelSx = {
    color: labelColor,
    backgroundColor: notchBg,
    px: 0.5,
    '&.Mui-focused': { color: colors.greenAccent[isDark ? 400 : 600] },
  };
  return {
    InputLabelProps: { shrink: true, sx: labelSx },
    sx: {
      '& .MuiOutlinedInput-root': {
        '& fieldset': { borderColor: borderMuted },
        '&:hover fieldset': { borderColor: borderHover },
        '&.Mui-focused fieldset': { borderColor: colors.greenAccent[500] },
      },
      '& .MuiOutlinedInput-input': { color: inputColor },
      '& .MuiInputLabel-root': labelSx,
      ...extraSx,
    },
  };
}

const TripReportEdit = () => {
  const { countryId, departureDate, guideId } = useParams();
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');

  const adminUserJson = localStorage.getItem('adminUser');
  const adminUser = adminUserJson ? JSON.parse(adminUserJson) : null;
  const isCoordinator = Number(adminUser?.role) === 3;

  const [tab, setTab] = useState(0);
  const [data, setData] = useState(null);
  const [openToast, setOpenToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');

  const fetchDetails = async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/${countryId}/${departureDate}?guide_id=${guideId}`,
        {
          headers: { token },
        }
      );
      const json = await res.json();
      if (json.success) setData(json.data);
      else {
        setToastMessage(json.message || 'Failed to fetch details');
        setOpenToast(true);
      }
    } catch (e) {
      setToastMessage('Network error fetching trip details');
      setOpenToast(true);
    }
  };

  useEffect(() => {
    fetchDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId, departureDate, guideId]);

  return (
    <Box m="20px">
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Header title="EDIT TRIP REPORT" subtitle={`${countryId} • ${departureDate}`} />
        {isCoordinator ? (
          <Button
            component={Link}
            to="/guide"
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 20px',
              '&:hover': { backgroundColor: colors.blueAccent[800] },
            }}
          >
            Go Back
          </Button>
        ) : (
          <Button
            component={Link}
            to="/trip-reports"
            sx={{
              backgroundColor: colors.blueAccent[700],
              color: colors.grey[100],
              fontSize: '14px',
              fontWeight: 'bold',
              padding: '10px 20px',
              '&:hover': { backgroundColor: colors.blueAccent[800] },
            }}
          >
            Go Back
          </Button>
        )}
      </Box>
      {data && (
        <Box mt={4} p={2} sx={{ background: colors.primary[500], borderRadius: 0, boxShadow: 'none', mb: 1 }}>
          <Box
            display="grid"
            gridTemplateColumns={{
              xs: 'repeat(2, 1fr)',
              sm: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            }}
            gap={2}
          >
            <SummaryCard title="Additional income" value={sumAmounts(data.sections.income)} />
            <SummaryCard title="Destination-related costs" value={sumAmounts(data.sections.excursion_expense)} />
            <SummaryCard title="Other income" value={sumAmounts(data.sections.travel_expense)} />
            <SummaryCard title="Other expenses" value={sumAmounts(data.sections.guide_expense)} />
          </Box>
        </Box>
      )}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          mb: 2,
          '& .MuiTab-root': {
            minWidth: 140,
            maxWidth: 280,
            whiteSpace: 'normal',
            lineHeight: 1.2,
            textTransform: 'none',
            fontWeight: 600,
            color: colors.grey[300],
            opacity: 1,
            '&.Mui-selected': {
              color: colors.greenAccent[400],
            },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: colors.greenAccent[500],
          },
        }}
      >
        <Tab label="Additional income" />
        <Tab label="Destination-related costs" />
        <Tab label="Other income" />
        <Tab label="Other expenses" />
        <Tab label="Mileage" />
        <Tab label="Excursion mileage" />
        <Tab label="Trip description" />
        <Tab label="Guide allowance" />
      </Tabs>

      {!data ? (
        <Typography>Loading...</Typography>
      ) : (
        <Box>
          {tab === 4 ? (
            <MileageEditor
              mileage={data?.mileage}
              onSaved={() => fetchDetails()}
              countryId={countryId}
              departureDate={departureDate}
              guideId={guideId}
            />
          ) : tab === 5 ? (
            <ExcursionMileageEditor
              initialRows={getReportGuide2(data, guideId)?.sections?.excursion_mileage}
              onSaved={() => fetchDetails()}
              countryId={countryId}
              departureDate={departureDate}
              guideId={guideId}
            />
          ) : tab === 6 ? (
            <TripDescriptionEditor
              initialText={getReportGuide2(data, guideId)?.sections?.trip_description ?? ''}
              onSaved={() => fetchDetails()}
              countryId={countryId}
              departureDate={departureDate}
              guideId={guideId}
            />
          ) : tab === 7 ? (
            <GuideAllowanceEditor
              initialAmount={
                getReportGuide2(data, guideId)?.guideAllowanceTotal ??
                getReportGuide2(data, guideId)?.sections?.guide_allowance_total ??
                0
              }
              onSaved={() => fetchDetails()}
              countryId={countryId}
              departureDate={departureDate}
              guideId={guideId}
            />
          ) : (
            <SectionEditor
              section={['income', 'excursion_expense', 'travel_expense', 'guide_expense'][tab]}
              rows={
                tab === 0
                  ? data.sections.income
                  : tab === 1
                  ? data.sections.excursion_expense
                  : tab === 2
                  ? data.sections.travel_expense
                  : data.sections.guide_expense
              }
              onSaved={() => fetchDetails()}
              countryId={countryId}
              departureDate={departureDate}
              guideId={guideId}
            />
          )}
        </Box>
      )}

      <Snackbar
        open={openToast}
        autoHideDuration={4000}
        onClose={() => setOpenToast(false)}
        message={toastMessage}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
    </Box>
  );
};

export default TripReportEdit;

/** @param {any} data trip details API payload */
function getReportGuide2(data, guideId) {
  const guides = Array.isArray(data?.reportGuides) ? data.reportGuides : [];
  const g = guides.find((x) => String(x?.guideId) === String(guideId));
  return g?.reportGuide2 ?? null;
}

const sumAmounts = (rows = []) => rows.reduce((acc, r) => acc + Number(r.amount || 0), 0);

const SummaryCard = ({ title, value }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <Box width="100%" bgcolor={colors.primary[400]} p={2} borderRadius={2}>
      <Box display="flex" justifyContent="space-between">
        <Box>
          <Typography variant="h3" fontWeight="bold" sx={{ color: colors.grey[100] }}>
            € {value.toFixed(2)}
          </Typography>
        </Box>
      </Box>
      <Box display="flex" justifyContent="space-between" mt="2px">
        <Typography variant="h4" sx={{ color: colors.greenAccent[500] }}>
          {title}
        </Typography>
      </Box>
    </Box>
  );
};

const SectionEditor = ({ section, rows = [], onSaved, countryId, departureDate, guideId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');
  const mapRow = (r) => ({
    id: r.id,
    name: r.name,
    amount: r.amount,
    entryDate: r.entryDate != null && r.entryDate !== '' ? String(r.entryDate).slice(0, 10) : '',
  });
  const [items, setItems] = useState(rows.map(mapRow));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setItems(rows.map(mapRow));
  }, [rows]);

  const addRow = () =>
    setItems((items) => [...items, { id: 'temp_' + Date.now(), name: '', amount: '', entryDate: '' }]);
  const removeRow = (id) => setItems((items) => items.filter((i) => i.id !== id));
  const updateRow = (id, key, val) => setItems((items) => items.map((i) => (i.id === id ? { ...i, [key]: val } : i)));

  const saveAll = async () => {
    setSaving(true);
    try {
      const body = {
        guide_id: guideId,
        section,
        items: items
          .filter(({ name, amount }) => name !== '' && amount !== '')
          .map(({ name, amount, entryDate }) => {
            const row = { name, amount: Number(amount || 0) };
            if (entryDate && String(entryDate).trim()) {
              row.entry_date = String(entryDate).trim().slice(0, 10);
            }
            return row;
          }),
      };
      await fetch(`${process.env.REACT_APP_APILINK}/api/admin/trip-reports/${countryId}/${departureDate}/entries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', token },
        body: JSON.stringify(body),
      });
      if (onSaved) onSaved();
    } catch {}
    setSaving(false);
  };

  const sectionTitle =
    section === 'income'
      ? 'Additional income'
      : section === 'excursion_expense'
      ? 'Destination-related costs'
      : section === 'travel_expense'
      ? 'Other income'
      : section === 'guide_expense'
      ? 'Other expenses'
      : section.replace('_', ' ');

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        {sectionTitle}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1, opacity: 0.85 }}>
        Fields: name, date, amount (per PDF). Date is optional.
      </Typography>
      {items.map((it) => (
        <Box
          key={it.id}
          display="grid"
          gap="12px"
          gridTemplateColumns={{ xs: '1fr', md: '2fr 1fr 1fr auto' }}
          alignItems="center"
          mb={1}
        >
          <TextField
            label="Name"
            value={it.name}
            onChange={(e) => updateRow(it.id, 'name', e.target.value)}
            {...tripReportOutlinedTextFieldProps(colors, theme)}
          />
          <TextField
            label="Date"
            type="date"
            value={it.entryDate}
            onChange={(e) => updateRow(it.id, 'entryDate', e.target.value)}
            {...tripReportOutlinedTextFieldProps(colors, theme)}
          />
          <TextField
            label="Amount"
            type="number"
            value={it.amount}
            onChange={(e) => updateRow(it.id, 'amount', e.target.value)}
            placeholder="0.00"
            inputProps={{ step: '0.01', min: 0 }}
            {...tripReportOutlinedTextFieldProps(colors, theme)}
          />
          <Box display="flex" gap={1}>
            <Button variant="outlined" color="error" onClick={() => removeRow(it.id)}>
              Remove
            </Button>
          </Box>
        </Box>
      ))}
      <Box display="flex" gap={1} mt={1}>
        <Button variant="outlined" onClick={addRow} sx={{ color: colors.grey[100], borderColor: colors.grey[300] }}>
          + Add
        </Button>
        <Button variant="contained" onClick={saveAll} disabled={saving}>
          Save
        </Button>
      </Box>
    </Box>
  );
};

const MileageEditor = ({ mileage, onSaved, countryId, departureDate, guideId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const garage = mileage?.mileageGarage ?? mileage?.startingPoint ?? '';
  const departurePt = mileage?.mileageDeparturePoint ?? mileage?.departurePoint ?? '';
  const final = mileage?.mileageFinal ?? mileage?.dropoffPoint ?? '';
  const [g, setG] = useState(garage === null || garage === undefined ? '' : String(garage));
  const [d, setD] = useState(departurePt === null || departurePt === undefined ? '' : String(departurePt));
  const [f, setF] = useState(final === null || final === undefined ? '' : String(final));
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    const ng = mileage?.mileageGarage ?? mileage?.startingPoint;
    const nd = mileage?.mileageDeparturePoint ?? mileage?.departurePoint;
    const nf = mileage?.mileageFinal ?? mileage?.dropoffPoint;
    setG(ng === null || ng === undefined ? '' : String(ng));
    setD(nd === null || nd === undefined ? '' : String(nd));
    setF(nf === null || nf === undefined ? '' : String(nf));
  }, [mileage]);

  const saveMileage = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/${countryId}/${departureDate}/mileage`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', token },
          body: JSON.stringify({
            guide_id: guideId,
            starting_point: Number(g || 0),
            mileage_departure_point: Number(d || 0),
            dropoff_point: Number(f || 0),
          }),
        }
      );
      const json = await res.json();
      if (json.success && onSaved) onSaved();
    } catch {}
    setSaving(false);
  };

  const net =
    g !== '' && f !== '' && !Number.isNaN(Number(g)) && !Number.isNaN(Number(f))
      ? Math.max(0, Math.round(Number(f) - Number(g)))
      : mileage?.mileageInvoice ?? mileage?.netMileage ?? '';

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Mileage
      </Typography>
      <Box display="grid" gap="16px" gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }} alignItems="center">
        <TextField
          label="1. Initial mileage from garage"
          type="number"
          value={g}
          onChange={(e) => setG(e.target.value)}
          {...tripReportOutlinedTextFieldProps(colors, theme)}
        />
        <TextField
          label="2. Initial mileage from departure point"
          type="number"
          value={d}
          onChange={(e) => setD(e.target.value)}
          {...tripReportOutlinedTextFieldProps(colors, theme)}
        />
        <TextField
          label="3. Final mileage (after passengers exit)"
          type="number"
          value={f}
          onChange={(e) => setF(e.target.value)}
          {...tripReportOutlinedTextFieldProps(colors, theme)}
        />
        <TextField
          label="4. Mileage for invoice (= 3 − 1)"
          value={net === '' ? '' : String(net)}
          InputProps={{ readOnly: true }}
          {...tripReportOutlinedTextFieldProps(colors, theme)}
        />
        <Box gridColumn={{ sm: '1 / -1' }}>
          <Button variant="contained" onClick={saveMileage} disabled={saving}>
            Save Mileage
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

/** Stable fallback — do not use `initialRows = []` in props (new [] every render breaks useEffect deps). */
const EXCURSION_MILEAGE_EMPTY = [];

const ExcursionMileageEditor = ({ initialRows, onSaved, countryId, departureDate, guideId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');
  const normalizeRows = (rows) =>
    (Array.isArray(rows) ? rows : []).map((r, i) => ({
      key: r.id != null ? `id_${r.id}` : `idx_${i}`,
      excursionName: r.excursionName || '',
      distanceTraveled: r.distanceTraveled != null ? String(r.distanceTraveled) : '',
      entryDate: r.entryDate ? String(r.entryDate).slice(0, 10) : '',
    }));
  const [rows, setRows] = useState(() => normalizeRows(initialRows ?? EXCURSION_MILEAGE_EMPTY));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setRows(normalizeRows(initialRows ?? EXCURSION_MILEAGE_EMPTY));
  }, [initialRows]);

  const addRow = () =>
    setRows((r) => [...r, { key: 'temp_' + Date.now(), excursionName: '', distanceTraveled: '', entryDate: '' }]);
  const removeRow = (key) => setRows((r) => r.filter((x) => x.key !== key));
  const update = (key, field, val) =>
    setRows((r) => r.map((x) => (x.key === key ? { ...x, [field]: val } : x)));

  const saveAll = async () => {
    setSaving(true);
    try {
      const items = rows
        .filter((x) => String(x.excursionName).trim() !== '')
        .map((x) => ({
          excursion_name: String(x.excursionName).trim(),
          distance_traveled: Number(x.distanceTraveled) || 0,
          entry_date: x.entryDate ? String(x.entryDate).slice(0, 10) : undefined,
        }));
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/${countryId}/${departureDate}/excursion-mileage`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', token },
          body: JSON.stringify({ guide_id: guideId, items }),
        }
      );
      const json = await res.json();
      if (json.success && onSaved) onSaved();
    } catch {}
    setSaving(false);
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
        Excursion mileage — fields: excursion name, distance traveled, date. Saving replaces all rows for this trip
        and guide.
      </Typography>
      {rows.map((row) => (
        <Box
          key={row.key}
          display="grid"
          gap="12px"
          gridTemplateColumns={{ xs: '1fr', md: '2fr 1fr 1fr auto' }}
          alignItems="center"
          mb={1}
        >
          <TextField
            label="Excursion name"
            value={row.excursionName}
            onChange={(e) => update(row.key, 'excursionName', e.target.value)}
            {...tripReportOutlinedTextFieldProps(colors, theme)}
          />
          <TextField
            label="Distance traveled"
            type="number"
            value={row.distanceTraveled}
            onChange={(e) => update(row.key, 'distanceTraveled', e.target.value)}
            placeholder="0"
            inputProps={{ step: 'any', min: 0 }}
            {...tripReportOutlinedTextFieldProps(colors, theme)}
          />
          <TextField
            label="Date"
            type="date"
            value={row.entryDate}
            onChange={(e) => update(row.key, 'entryDate', e.target.value)}
            {...tripReportOutlinedTextFieldProps(colors, theme)}
          />
          <Button variant="outlined" color="error" onClick={() => removeRow(row.key)}>
            Remove
          </Button>
        </Box>
      ))}
      <Box display="flex" gap={1} mt={1}>
        <Button variant="outlined" onClick={addRow} sx={{ color: colors.grey[100], borderColor: colors.grey[300] }}>
          + Add row
        </Button>
        <Button variant="contained" onClick={saveAll} disabled={saving}>
          Save
        </Button>
      </Box>
    </Box>
  );
};

const TripDescriptionEditor = ({ initialText = '', onSaved, countryId, departureDate, guideId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');
  const [text, setText] = useState(initialText || '');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setText(initialText || '');
  }, [initialText]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/${countryId}/${departureDate}/trip-description`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', token },
          body: JSON.stringify({ guide_id: guideId, description: text }),
        }
      );
      const json = await res.json();
      if (json.success && onSaved) onSaved();
    } catch {}
    setSaving(false);
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
        Text field where the guide enters details about the trip, issues, advantages, etc.
      </Typography>
      <TextField
        label="Trip description"
        multiline
        minRows={6}
        fullWidth
        value={text}
        onChange={(e) => setText(e.target.value)}
        {...tripReportOutlinedTextFieldProps(colors, theme, { mb: 2 })}
      />
      <Button variant="contained" onClick={save} disabled={saving}>
        Save
      </Button>
    </Box>
  );
};

const GuideAllowanceEditor = ({ initialAmount = 0, onSaved, countryId, departureDate, guideId }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const token = localStorage.getItem('token');
  const [amount, setAmount] = useState(
    Number.isFinite(Number(initialAmount)) ? String(Number(initialAmount).toFixed(2)) : '0.00'
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const n = Number(initialAmount);
    setAmount(Number.isFinite(n) ? n.toFixed(2) : '0.00');
  }, [initialAmount]);

  const save = async () => {
    const numericAmount = Number(amount);
    if (!Number.isFinite(numericAmount) || numericAmount < 0) return;
    setSaving(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_APILINK}/api/admin/trip-reports/${countryId}/${departureDate}/guide-allowance`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', token },
          body: JSON.stringify({ guide_id: guideId, total_amount: Number(numericAmount.toFixed(2)) }),
        }
      );
      const json = await res.json();
      if (json.success && onSaved) onSaved();
    } catch {}
    setSaving(false);
  };

  return (
    <Box>
      <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
        Manual guide allowance total for this trip.
      </Typography>
      <TextField
        label="Guide allowance total"
        type="number"
        value={amount}
        onChange={(e) => setAmount(e.target.value)}
        inputProps={{ step: '0.01', min: 0 }}
        {...tripReportOutlinedTextFieldProps(colors, theme, { mb: 2, maxWidth: 320 })}
      />
      <Box>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </Box>
    </Box>
  );
};
